import React, { useState } from 'react';
import {
  Search,
  LayoutGrid,
  UserCheck,
  Settings,
  CreditCard,
  ShieldCheck,
  Headphones,
  Plus,
  Minus,
  Mail,
  ChevronRight,
  BookOpen,
  Video,
  Activity,
  Users,
  Calendar,
  ArrowRight
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';
import useFaqs from '../../hooks/features/vitrine/faqs/useFaqs';

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {faqs} = useFaqs()

  const categories = [
    { id: 'all', label: 'Toutes les catégories', icon: LayoutGrid },
    { id: 'account', label: 'Compte & Inscription', icon: UserCheck },
    { id: 'features', label: 'Services & Fonctionnalités', icon: Settings },
    { id: 'pricing', label: 'Paiements & Tarifs', icon: CreditCard },
    { id: 'security', label: 'Sécurité & Confidentialité', icon: ShieldCheck },
    { id: 'support', label: 'Support & Assistance', icon: Headphones },
  ];

  

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <PublicPageLayout>
        <div className="px-6 lg:px-12 py-6 mx-auto space-y-6">

          {/* ================= HERO SECTION (Directement sur le bg de la page) ================= */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-1 pb-2">
            
            {/* Gauche: Texte & Barre de Recherche */}
            <div className="lg:col-span-6 space-y-6">
              <div className="space-y-3">
                <span className="text-xs font-extrabold text-blue-600 tracking-wider uppercase">
                  FAQ
                </span>
                <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
                  Des réponses claires <br />
                  à toutes vos <span className="text-blue-600">questions.</span>
                </h1>
                <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                  Trouvez rapidement des réponses aux questions les plus fréquentes sur nos services, nos fonctionnalités et votre compte.
                </p>
              </div>

              {/* Barre de Recherche Blanc Pur */}
              <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Rechercher une question..."
                  className="w-full bg-white border border-slate-200/80 rounded-2xl pl-11 pr-4 py-3.5 text-sm outline-none focus:border-blue-500 transition-all shadow-sm"
                />
              </div>
            </div>

            {/* Droite: Illustration FAQ avec le dégradé en filigrane */}
            <div className="lg:col-span-6 flex justify-center items-center relative">
              <div className="relative w-full max-w-md aspect-[4/3] flex items-center justify-center">
                {/* Formes en filigrane / gradient de fond */}
                <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-blue-100/60 to-purple-100/40 rounded-full blur-3xl -z-10" />
                
                {/* Image illustrative transparente */}
                <img
                  src="/faq/hero.png" // Remplace par ton asset d'illustration 3D
                  alt="FAQ Illustration"
                  className="w-full h-full object-contain relative z-10"
                />
              </div>
            </div>

          </div>

          {/* ================= BARRE DE CATEGORIES (Visibles en grille) ================= */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center justify-center gap-2 px-3 py-3.5 rounded-2xl text-xs font-bold transition-all text-center ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                      : 'bg-white text-slate-600 border border-slate-200/60 hover:bg-slate-50 shadow-sm'
                  }`}
                >
                  <Icon size={16} className={`shrink-0 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                  <span className="truncate">{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* ================= CONTENU PRINCIPAL (ACCORDÉON + SIDEBAR) ================= */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Gauche: Accordéon FAQ (Carte Blanche) */}
            <div className="lg:col-span-8 bg-white rounded-3xl border border-slate-200/70 p-6 lg:p-8 shadow-sm space-y-4">
              {faqs.map((faq, index) => {
                const isOpen = openIndex === index;
                return (
                  <div
                    key={index}
                    className="border-b border-slate-100 last:border-none pb-4 last:pb-0 transition-colors"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full flex items-center justify-between text-left gap-4 py-2 group"
                    >
                      <span className="font-bold text-slate-900 text-sm lg:text-base group-hover:text-blue-600 transition-colors">
                        {faq.question}
                      </span>
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                          isOpen ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                        }`}
                      >
                        {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="pt-2 pb-1 text-xs lg:text-sm text-slate-500 leading-relaxed max-w-2xl">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Droite: Sidebar Support & Ressources */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Widget 1: Pas de réponse trouvée */}
              <div className="bg-white rounded-3xl border border-slate-200/70 p-6 shadow-sm space-y-4">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Vous ne trouvez pas votre réponse ?</h3>
                  <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                    Notre équipe support est là pour vous aider. Contactez-nous directement, nous vous répondrons dans les plus brefs délais.
                  </p>
                </div>

                <div className="space-y-2.5 pt-1">
                  {/* Option 1: Chat en direct */}
                  <a
                    href="#chat"
                    className="flex items-center justify-between p-3 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Headphones size={18} />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors">Chat en direct</h4>
                        <p className="text-[10px] text-slate-400 font-medium">Disponible 24h/24 et 7j/7</p>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                  </a>

                  {/* Option 2: Envoyer un e-mail */}
                  <a
                    href="mailto:support@izicallcenter.com"
                    className="flex items-center justify-between p-3 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <Mail size={18} />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors">Envoyer un e-mail</h4>
                        <p className="text-[10px] text-blue-600 font-semibold">support@izicallcenter.com</p>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                  </a>
                </div>
              </div>

              {/* Widget 2: Ressources utiles */}
              <div className="bg-white rounded-3xl border border-slate-200/70 p-6 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900 text-sm">Ressources utiles</h3>

                <div className="space-y-1">
                  {[
                    { icon: BookOpen, label: "Guides d'utilisation" },
                    { icon: Video, label: "Tutoriels vidéo" },
                    { icon: Activity, label: "État des services" },
                    { icon: Users, label: "Communauté d'utilisateurs" },
                  ].map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <a
                        key={idx}
                        href="#"
                        className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={16} className="text-blue-600" />
                          <span className="text-xs font-semibold text-slate-700 group-hover:text-slate-900">
                            {item.label}
                          </span>
                        </div>
                        <ChevronRight size={14} className="text-slate-400 group-hover:text-slate-600" />
                      </a>
                    );
                  })}
                </div>
              </div>

            </div>

          </div>

          {/* ================= BANNIÈRE CTA BAS DE PAGE ================= */}
          <div className="bg-[#031533] rounded-3xl p-6 lg:p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg">
            
            <div className="flex items-center gap-4 text-center md:text-left">
              <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Headphones size={22} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg lg:text-xl font-bold">
                  Besoin d'une assistance personnalisée ?
                </h3>
                <p className="text-slate-400 text-xs lg:text-sm mt-0.5">
                  Nos experts sont disponibles pour vous accompagner pas à pas.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 w-full md:w-auto">
              <button className="border border-slate-600 hover:bg-slate-800 text-white font-semibold text-xs px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors whitespace-nowrap">
                Demander une démo <Calendar size={14} />
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap">
                Nous contacter <ArrowRight size={14} />
              </button>
            </div>

          </div>

        </div>
    </PublicPageLayout>
  );
}